import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import api, { getApiErrorMessage } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Receipt, Search, Plus, FileText, Upload, ChevronDown, Download,
  FileSpreadsheet, Check, X, AlertTriangle, XCircle, Package, BarChart3
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import InteractiveTable from '../components/InteractiveTable';
import InvoiceDashboard from './Invoices/InvoiceDashboard';
import ModuleReports from './Reports/ModuleReports';
import { INVOICE_REPORTS } from './Reports/reportConfigs';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700', MATCHED: 'bg-green-100 text-green-700',
  VARIANCE: 'bg-amber-100 text-amber-700', PENDING_APPROVAL: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-emerald-100 text-emerald-700', REJECTED: 'bg-red-100 text-red-700',
  PARTIALLY_PAID: 'bg-cyan-100 text-cyan-700', PAID: 'bg-green-200 text-green-800',
  READY_FOR_PAYMENT: 'bg-teal-100 text-teal-700',
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();
  const { systemSettings } = useAuth();
  const currency = systemSettings?.system_currency || 'USD';

  // Action menu
  const [menuOpen, setMenuOpen] = useState(false);

  // Manual entry state
  const [showManual, setShowManual] = useState(false);
  const [newInv, setNewInv] = useState({ supplier_invoice_number: '', supplier_code: '', supplier_name: '', order_number: '', invoice_date: '', due_date: '' });
  const [suppliers, setSuppliers] = useState([]);
  const [creating, setCreating] = useState(false);

  // OCR state
  const [showOCR, setShowOCR] = useState(false);
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [poSearchResults, setPoSearchResults] = useState([]);

  // Spreadsheet import state
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importParsed, setImportParsed] = useState(null); // { columns, preview, suggested_mappings, row_count }
  const [importMappings, setImportMappings] = useState({});
  const [importStep, setImportStep] = useState('upload'); // upload, map, preview
  const [importDefaultPO, setImportDefaultPO] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Tab state
  const [activeView, setActiveView] = useState('dashboard'); // 'invoices' or 'email-ingest' or 'dashboard'
  const [invSort, handleInvSort] = useSortState('invoice_ref', 'desc');
  const [ingestSort, handleIngestSort] = useSortState('received_at', 'desc');

  // Email ingest queue
  const [ingestQueue, setIngestQueue] = useState([]);
  const [ingestTotal, setIngestTotal] = useState(0);
  const [ingestSummary, setIngestSummary] = useState({});
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestSearch, setIngestSearch] = useState('');
  const [ingestStatusFilter, setIngestStatusFilter] = useState('');

  const loadInvoices = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    params.set('page_size', '50');
    api.get(`/api/invoices/?${params}`).then(r => setInvoices(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const loadIngestQueue = useCallback(() => {
    setIngestLoading(true);
    const params = new URLSearchParams({ page_size: '50' });
    if (ingestSearch) params.set('search', ingestSearch);
    if (ingestStatusFilter) params.set('status', ingestStatusFilter);
    api.get(`/api/invoices/email-ingest/queue?${params}`).then(r => {
      setIngestQueue(r.data.data || []);
      setIngestTotal(r.data.total || 0);
      setIngestSummary(r.data.status_summary || {});
    }).catch(() => {}).finally(() => setIngestLoading(false));
  }, [ingestSearch, ingestStatusFilter]);

  useEffect(() => { if (activeView === 'email-ingest') loadIngestQueue(); }, [activeView, loadIngestQueue]);

  const fmtCurrency = (v) => `${currency} ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const reprocessIngest = async (queueId) => {
    try {
      const r = await api.post(`/api/invoices/email-ingest/${queueId}/reprocess`);
      toast.success(r.data.message);
      loadIngestQueue();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Reprocess failed')); }
  };

  const dismissIngest = async (queueId) => {
    try {
      await api.post(`/api/invoices/email-ingest/${queueId}/dismiss`);
      toast.success('Entry dismissed');
      loadIngestQueue();
    } catch { toast.error('Failed'); }
  };

  // Simulate email receipt (for testing / manual upload to ingest queue)
  const simulateEmailIngest = async (file) => {
    if (!file) return;
    setCreating(true);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
      const r = await api.post('/api/invoices/email-ingest/receive', {
        sender_email: 'manual-upload@system',
        sender_name: 'Manual Upload',
        subject: `Invoice: ${file.name}`,
        file_data: fileData,
        file_name: file.name,
      });
      toast.success(r.data.message);
      loadIngestQueue();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Ingest failed')); }
    setCreating(false);
  };

  // ── Manual entry ──
  const openManual = () => {
    const today = new Date().toISOString().split('T')[0];
    setNewInv({ supplier_invoice_number: '', supplier_code: '', supplier_name: '', order_number: '', invoice_date: today, due_date: today });
    api.get('/api/data/suppliers?page_size=200').then(r => setSuppliers(r.data.data || [])).catch(() => {});
    setShowManual(true);
    setMenuOpen(false);
  };

  const createInvoice = async () => {
    if (!newInv.supplier_invoice_number.trim()) { toast.error('Supplier invoice number is required'); return; }
    if (!newInv.supplier_code) { toast.error('Supplier is required'); return; }
    setCreating(true);
    try {
      const r = await api.post('/api/invoices/', newInv);
      toast.success(r.data.message);
      setShowManual(false);
      navigate(`/invoices/${r.data.invoice_ref}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setCreating(false);
  };

  // ── OCR ──
  const openOCR = () => {
    setShowOCR(true);
    setOcrFile(null);
    setOcrResult(null);
    setPoSearchQuery('');
    setPoSearchResults([]);
    setMenuOpen(false);
  };

  const processOCR = async (file) => {
    if (!file) return;
    setOcrProcessing(true);
    setOcrResult(null);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
      const r = await api.post('/api/invoices/ocr/process', { file_data: fileData, file_name: file.name, order_number: '' });
      setOcrResult(r.data);
      if (r.data.warnings?.length > 0) {
        r.data.warnings.forEach(w => {
          if (w.severity === 'error') toast.error(w.message);
          else toast.warning(w.message);
        });
      }
      if (r.data.po_found) {
        toast.success(`Matched PO ${r.data.po_data.order_number}`);
      } else if (r.data.suggested_pos?.length) {
        toast.info(`${r.data.suggested_pos.length} possible PO match(es) found.`);
      } else {
        toast.warning('No matching purchase order found.');
      }
    } catch (e) { toast.error(getApiErrorMessage(e, 'OCR processing failed')); }
    setOcrProcessing(false);
  };

  const createFromOCR = async (useOrderNumber, createGhost = false) => {
    if (!ocrResult?.extracted) return;
    const overrideWarnings = (ocrResult.warnings || []).filter(w => w.requires_override);
    if (overrideWarnings.length > 0) {
      const msg = overrideWarnings.map(w => w.message).join('\n\n') + '\n\nDo you want to override and proceed?';
      if (!window.confirm(msg)) return;
    }
    setCreating(true);
    try {
      const r = await api.post('/api/invoices/ocr/create', {
        extracted: ocrResult.extracted, order_number: useOrderNumber || '', create_ghost_po: createGhost,
        overrides: overrideWarnings.map(w => w.type),
      });
      toast.success(r.data.message);
      if (r.data.invoice_ref) {
        try { await api.post(`/api/invoices/${r.data.invoice_ref}/match`); } catch {}
      }
      setShowOCR(false); setOcrResult(null); setOcrFile(null);
      loadInvoices();
      if (r.data.invoice_ref) navigate(`/invoices/${r.data.invoice_ref}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create invoice')); }
    setCreating(false);
  };

  const searchPOs = async () => {
    if (!poSearchQuery.trim()) return;
    try {
      const r = await api.get(`/api/orders/?search=${encodeURIComponent(poSearchQuery)}&page_size=10`);
      setPoSearchResults(r.data.data || []);
    } catch { setPoSearchResults([]); }
  };

  // ── Spreadsheet import ──
  const openImport = () => {
    setShowImport(true);
    setImportFile(null);
    setImportParsed(null);
    setImportMappings({});
    setImportStep('upload');
    setImportDefaultPO('');
    setImportResult(null);
    setMenuOpen(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/invoices/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'invoice_import_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const parseImportFile = async (file) => {
    if (!file) return;
    setImportFile(file);
    setCreating(true);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
      const r = await api.post('/api/invoices/import/parse', { file_data: fileData, file_name: file.name });
      setImportParsed(r.data);
      setImportMappings(r.data.suggested_mappings || {});
      setImportStep('map');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to parse file')); }
    setCreating(false);
  };

  const runImport = async () => {
    if (!importParsed) return;
    setImporting(true);
    try {
      const r = await api.post('/api/invoices/import/create', {
        rows: importParsed.rows || importParsed.preview,
        mappings: importMappings,
        default_order_number: importDefaultPO,
      });
      setImportResult(r.data);
      setImportStep('result');
      if (r.data.created_count > 0) toast.success(`${r.data.created_count} invoice(s) imported`);
      if (r.data.error_count > 0) toast.warning(`${r.data.error_count} error(s)`);
      loadInvoices();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Import failed')); }
    setImporting(false);
  };

  const INVOICE_FIELDS = [
    { key: 'supplier_invoice_number', label: 'Supplier Invoice Number', required: true },
    { key: 'supplier_code', label: 'Supplier Code', required: true },
    { key: 'supplier_name', label: 'Supplier Name' },
    { key: 'order_number', label: 'Purchase Order Number' },
    { key: 'invoice_date', label: 'Invoice Date', required: true },
    { key: 'due_date', label: 'Due Date' },
    { key: 'currency', label: 'Currency' },
    { key: 'item_description', label: 'Item Description', required: true },
    { key: 'quantity', label: 'Quantity', required: true },
    { key: 'unit_price', label: 'Unit Price', required: true },
    { key: 'net_price', label: 'Net Price' },
    { key: 'unit', label: 'Unit' },
    { key: 'tax', label: 'Tax' },
    { key: 'notes', label: 'Notes' },
  ];

  const invoiceDefaultCols = useMemo(() => [
    { key: 'invoice_ref', label: 'Ref', width: 100, sortField: 'invoice_ref', visible: true },
    { key: 'supplier_invoice_number', label: 'Supplier Invoice #', width: 160, sortField: 'supplier_invoice_number', visible: true },
    { key: 'supplier_name', label: 'Supplier', width: 180, sortField: 'supplier_name', visible: true },
    { key: 'order_number', label: 'PO', width: 110, sortField: 'order_number', visible: true },
    { key: 'invoice_date', label: 'Date', width: 110, sortField: 'invoice_date', visible: true },
    { key: 'invoice_gross_total', label: 'Gross Total', width: 130, sortField: 'invoice_gross_total', align: 'right', visible: true },
    { key: 'amount_paid', label: 'Paid', width: 120, sortField: 'amount_paid', align: 'right', visible: true },
    { key: 'match_status', label: 'Match', width: 100, sortField: 'match_status', visible: true },
    { key: 'status', label: 'Status', width: 120, sortField: 'status', visible: true },
  ], []);

  const renderInvoiceCell = useCallback((col, inv) => {
    switch (col.key) {
      case 'invoice_ref': return <span className="text-zinc-900 font-semibold text-sm">{inv.invoice_ref}</span>;
      case 'supplier_invoice_number': return <span className="text-sm">{inv.supplier_invoice_number}</span>;
      case 'supplier_name': return <span className="text-sm">{inv.supplier_name || inv.supplier_code || '—'}</span>;
      case 'order_number': return inv.order_number ? <span className="text-sm">{inv.order_number}</span> : <span className="text-muted-foreground text-sm">No PO</span>;
      case 'invoice_date': return <span className="text-muted-foreground text-sm whitespace-nowrap">{inv.invoice_date}</span>;
      case 'invoice_gross_total': return <span className="text-sm tabular-nums">{fmtCurrency(inv.invoice_gross_total)}</span>;
      case 'amount_paid': return <span className="text-sm tabular-nums">{fmtCurrency(inv.amount_paid)}</span>;
      case 'match_status': return (
        <Badge className={`text-[9px] ${inv.match_status === 'MATCHED' ? 'bg-green-100 text-green-700' : inv.match_status === 'VARIANCE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
          {inv.match_status || 'PENDING'}
        </Badge>
      );
      case 'status': return <Badge className={`text-[9px] ${STATUS_COLORS[inv.status] || ''}`}>{(inv.status || '').replace(/_/g, ' ')}</Badge>;
      default: return <span className="text-sm">{inv[col.key] != null ? String(inv[col.key]) : '—'}</span>;
    }
  }, [fmtCurrency]);

  const sortedInvoices = useMemo(() => sortData(invoices, invSort.field, invSort.dir), [invoices, invSort]);

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            <Receipt className="inline h-5 w-5 mr-2 text-primary" />Invoice Matching
          </h1>
          <HelpButton guideId="invoice-matching" label="Invoice Matching Guide" />
        </div>
        <div className="flex items-center gap-2">
          {activeView === 'invoices' && (
            <>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="h-8 rounded-lg border border-input bg-background px-2 text-xs">
                <option value="">All Statuses</option>
                {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices..." className="pl-8 h-8 text-sm" />
              </div>
            </>
          )}
          {activeView === 'email-ingest' && (
            <>
              <select value={ingestStatusFilter} onChange={e => setIngestStatusFilter(e.target.value)}
                className="h-8 rounded-lg border border-input bg-background px-2 text-xs">
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSED">Processed</option>
                <option value="NEEDS_REVIEW">Needs Review</option>
                <option value="ERROR">Error</option>
                <option value="DISMISSED">Dismissed</option>
              </select>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
                <Input value={ingestSearch} onChange={e => setIngestSearch(e.target.value)} placeholder="Search email queue..." className="pl-8 h-8 text-sm" />
              </div>
            </>
          )}
          {/* Actions dropdown */}
          <div className="relative">
            <Button size="sm" onClick={() => setMenuOpen(!menuOpen)} data-testid="new-invoice-dropdown">
              <Plus className="h-4 w-4 mr-1" /> New Invoice <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-card border rounded-lg shadow-xl py-1 w-56">
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={openManual} data-testid="manual-invoice-btn">
                  <FileText className="h-3.5 w-3.5" /> Manual Entry
                </button>
                {(Array.isArray(systemSettings?.tenant_modules) ? systemSettings.tenant_modules : []).includes('ocr') && (
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={openOCR} data-testid="ocr-invoice-btn">
                  <Search className="h-3.5 w-3.5" /> Upload Invoice (OCR)
                </button>
                )}
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={openImport} data-testid="import-invoice-btn">
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Import from Spreadsheet
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b mb-4">
        {[
          { id: 'dashboard', label: 'Analytics', count: 0 },
          { id: 'invoices', label: 'All Invoices', count: invoices.length },
          { id: 'email-ingest', label: 'Inbound Emailed Invoices Queue', count: ingestTotal },
          { id: 'reports', label: 'Reports', count: 0 },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveView(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeView === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.id === 'dashboard' && <BarChart3 className="h-3.5 w-3.5" />}
            {t.label}
            {t.count > 0 && <span className="ml-1.5 text-[10px] bg-muted rounded-full px-1.5 py-0.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Invoices list */}
      {activeView === 'invoices' && (loading ? <LoadingSpinner /> : invoices.length === 0 ? (
        <EmptyState title="No invoices" description="Create your first invoice using Manual Entry, OCR Upload, or Spreadsheet Import." />
      ) : (
        <InteractiveTable
          tableId="invoices-listing"
          defaultCols={invoiceDefaultCols}
          data={sortedInvoices}
          renderCell={renderInvoiceCell}
          onRowClick={(row) => navigate(`/invoices/${row.invoice_ref}`)}
          sortState={invSort}
          onSort={handleInvSort}
          rowKey="invoice_ref"
        />
      ))}

      {/* Email Ingest Queue */}
      {activeView === 'email-ingest' && (
        <div>
          {/* Status summary cards */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            {[
              { key: 'PENDING', label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' },
              { key: 'PROCESSED', label: 'Processed', color: 'bg-green-50 text-green-700 border-green-200' },
              { key: 'NEEDS_REVIEW', label: 'Needs Review', color: 'bg-red-50 text-red-700 border-red-200' },
              { key: 'ERROR', label: 'Errors', color: 'bg-red-50 text-red-700 border-red-200' },
              { key: 'DISMISSED', label: 'Dismissed', color: 'bg-gray-50 text-gray-600 border-gray-200' },
            ].map(s => (
              <button key={s.key} onClick={() => setIngestStatusFilter(ingestStatusFilter === s.key ? '' : s.key)}
                className={`border rounded-xl p-3 text-center transition-colors ${ingestStatusFilter === s.key ? s.color + ' ring-2 ring-primary/30' : 'bg-card hover:bg-accent/20'}`}>
                <p className="text-xl font-bold">{ingestSummary[s.key] || 0}</p>
                <p className="text-[10px]">{s.label}</p>
              </button>
            ))}
          </div>

          {/* Upload to simulate email */}
          <div className="mb-4 p-6 border-2 border-dashed rounded-xl text-center transition-colors"
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
            onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); const f = e.dataTransfer.files[0]; if (f) simulateEmailIngest(f); }}>
            <p className="text-xs text-muted-foreground mb-2">Drag & drop an invoice file here to add to the ingest queue, or click to browse.</p>
            <label className="cursor-pointer">
              <Button size="sm" variant="outline" asChild data-testid="simulate-email-ingest-btn">
                <span><Upload className="h-3.5 w-3.5 mr-1" /> Select File</span>
              </Button>
              <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => { const f = e.target.files?.[0]; if (f) simulateEmailIngest(f); }} />
            </label>
            {creating && <p className="text-xs text-muted-foreground mt-2">Processing...</p>}
          </div>

          {/* Queue table */}
          {ingestLoading ? <LoadingSpinner /> : ingestQueue.length === 0 ? (
            <EmptyState title="No email ingest entries" description="Invoices emailed to the ingest address will appear here." />
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-4 py-2.5 font-semibold">Received</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Sender</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Subject / File</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Invoice #</th>
                    <th className="text-left px-4 py-2.5 font-semibold">PO</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Created Invoice</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                    <th className="text-left px-4 py-2.5 font-semibold w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortData(ingestQueue, ingestSort.field, ingestSort.dir).map(q => {
                    const statusColors = {
                      PENDING: 'bg-amber-100 text-amber-700', PROCESSED: 'bg-green-100 text-green-700',
                      NEEDS_REVIEW: 'bg-red-100 text-red-700', ERROR: 'bg-red-100 text-red-700',
                      DISMISSED: 'bg-gray-100 text-gray-600', REPROCESSING: 'bg-blue-100 text-blue-700',
                    };
                    return (
                      <tr key={q.queue_id} className="border-b hover:bg-accent/20">
                        <td className="px-4 py-2 text-sm text-muted-foreground whitespace-nowrap">{q.received_at ? new Date(q.received_at).toLocaleString('en-GB', { timeZone: systemSettings?.timezone || 'America/Los_Angeles' }) : '—'}</td>
                        <td className="px-4 py-2 text-sm">{q.sender_email || '—'}</td>
                        <td className="px-4 py-2 text-sm max-w-[200px] truncate">{q.subject || q.file_name || '—'}</td>
                        <td className="px-4 py-2 text-sm font-semibold">{q.extracted_invoice_number || '—'}</td>
                        <td className="px-4 py-2 text-sm">{q.extracted_po_number || '—'}</td>
                        <td className="px-4 py-2 text-sm">
                          {q.invoice_ref ? (
                            <span className="text-primary font-semibold cursor-pointer hover:underline" onClick={() => navigate(`/invoices/${q.invoice_ref}`)}>{q.invoice_ref}</span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <Badge className={`text-[9px] ${statusColors[q.status] || 'bg-gray-100'}`}>{(q.status || '').replace(/_/g, ' ')}</Badge>
                          {q.error_message && <p className="text-[9px] text-red-600 mt-0.5 max-w-[150px] truncate" title={q.error_message}>{q.error_message}</p>}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            {['PENDING', 'ERROR', 'NEEDS_REVIEW'].includes(q.status) && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => reprocessIngest(q.queue_id)}>Retry</Button>
                            )}
                            {q.status !== 'DISMISSED' && q.status !== 'PROCESSED' && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-muted-foreground" onClick={() => dismissIngest(q.queue_id)}>Dismiss</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dashboard tab */}
      {activeView === 'dashboard' && <InvoiceDashboard onNavigate={(view, opts) => {
        if (opts?.status) setStatusFilter(opts.status);
        if (opts?.search) setSearch(opts.search);
        setActiveView(view);
      }} />}

      {/* Reports tab */}
      {activeView === 'reports' && <ModuleReports reports={INVOICE_REPORTS} apiPrefix="/api/module-reports/invoices" moduleLabel="Invoice Matching" />}

      {/* ── Manual Entry Dialog ── */}
      {showManual && (
        <Dialog open onOpenChange={() => setShowManual(false)}>
          <DialogContent className="max-w-lg" data-testid="manual-invoice-dialog">
            <DialogHeader><DialogTitle>Create New Invoice — Manual Entry</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-red-500">Supplier Invoice Number *</Label>
                <Input value={newInv.supplier_invoice_number} onChange={e => setNewInv(s => ({ ...s, supplier_invoice_number: e.target.value }))} className="h-8 text-sm" placeholder="e.g. INV-2024-001" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-red-500">Supplier *</Label>
                <select value={newInv.supplier_code} onChange={e => {
                  const sup = suppliers.find(s => s.supplier_code === e.target.value);
                  setNewInv(s => ({ ...s, supplier_code: e.target.value, supplier_name: sup?.supplier_name || '' }));
                }} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => <option key={s.supplier_code} value={s.supplier_code}>{s.supplier_name} ({s.supplier_code})</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Purchase Order (optional)</Label>
                <Input value={newInv.order_number} onChange={e => setNewInv(s => ({ ...s, order_number: e.target.value }))} className="h-8 text-sm" placeholder="e.g. PO000001 (leave blank for non-PO invoice)" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Date</Label>
                  <Input type="date" value={newInv.invoice_date} onChange={e => setNewInv(s => ({ ...s, invoice_date: e.target.value }))} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Due Date</Label>
                  <Input type="date" value={newInv.due_date} onChange={e => setNewInv(s => ({ ...s, due_date: e.target.value }))} className="h-8 text-sm" />
                </div>
              </div>
            </div>
            <div className="flex justify-start gap-2 mt-4">
              <Button onClick={createInvoice} disabled={creating} data-testid="create-manual-invoice-btn">{creating ? 'Creating...' : 'Create Invoice'}</Button>
              <Button variant="outline" onClick={() => setShowManual(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── OCR Dialog ── */}
      {showOCR && (
        <Dialog open onOpenChange={() => { setShowOCR(false); setOcrResult(null); setOcrFile(null); }}>
          <DialogContent className="max-w-[95vw] xl:max-w-5xl max-h-[90vh] flex flex-col p-0" data-testid="ocr-invoice-dialog">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>{ocrResult ? 'OCR Results — Review & Confirm' : 'Upload Invoice for OCR Processing'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {!ocrResult && (
                <div>
                  <div className="border-2 border-dashed rounded-xl p-10 text-center mb-4"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                    onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); setOcrFile(e.dataTransfer.files[0]); }}>
                    <p className="text-muted-foreground mb-3">{ocrFile ? ocrFile.name : 'Drag & drop invoice file here (PDF or image).'}</p>
                    <label>
                      <Button size="sm" variant={ocrFile ? 'outline' : 'default'} onClick={() => {}}><Upload className="h-3.5 w-3.5 mr-1" /> Select File</Button>
                      <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => setOcrFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  <div className="flex justify-start gap-2">
                    {ocrFile && <Button onClick={() => processOCR(ocrFile)} disabled={ocrProcessing}>{ocrProcessing ? 'Processing with AI...' : 'Process Invoice'}</Button>}
                    <Button variant="outline" onClick={() => { setShowOCR(false); setOcrFile(null); }}>Cancel</Button>
                  </div>
                </div>
              )}

              {ocrResult && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Extracted invoice data */}
                    <div className="rounded-lg p-4" style={{ background: '#eff6ff' }}>
                      <h4 className="text-xs font-bold text-blue-800 uppercase mb-3">Extracted Invoice Data</h4>
                      <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                        <div><p className="text-[10px] text-blue-600">Invoice #</p><p className="font-semibold">{ocrResult.extracted.invoice_number || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Supplier</p><p className="font-semibold">{ocrResult.extracted.supplier_name || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Invoice Total</p><p className="font-semibold">{ocrResult.extracted.currency || 'USD'} {fmtNum(ocrResult.extracted.total)}</p></div>
                        <div><p className="text-[10px] text-blue-600">Invoice Date</p><p>{ocrResult.extracted.invoice_date || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Due Date</p><p>{ocrResult.extracted.due_date || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">PO Reference</p><p>{ocrResult.extracted.purchase_order_number || 'None'}</p></div>
                      </div>
                      <table className="w-full text-[11px] border-collapse bg-white/70 rounded">
                        <thead><tr className="border-b bg-blue-100/50"><th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5 w-14">Qty</th><th className="text-right px-2 py-1.5 w-20">Price</th><th className="text-right px-2 py-1.5 w-24">Net</th></tr></thead>
                        <tbody>
                          {(ocrResult.extracted.line_items || []).map((li, i) => (
                            <tr key={i} className="border-b"><td className="px-2 py-1">{i+1}</td><td className="px-2 py-1">{li.item_description}</td><td className="px-2 py-1 text-right">{li.quantity}</td><td className="px-2 py-1 text-right">{fmtNum(li.unit_price)}</td><td className="px-2 py-1 text-right font-semibold">{fmtNum(li.net_price)}</td></tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t"><td colSpan={4} className="px-2 py-1 text-right text-[10px] text-muted-foreground">Subtotal</td><td className="px-2 py-1 text-right">{fmtNum(ocrResult.extracted.subtotal)}</td></tr>
                          <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Tax</td><td className="px-2 py-0.5 text-right">{fmtNum(ocrResult.extracted.tax)}</td></tr>
                          <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Shipping</td><td className="px-2 py-0.5 text-right">{fmtNum(ocrResult.extracted.shipping)}</td></tr>
                          <tr className="border-t-2 font-bold"><td colSpan={4} className="px-2 py-1.5 text-right">Total ({ocrResult.extracted.currency || 'USD'})</td><td className="px-2 py-1.5 text-right text-blue-700">{fmtNum(ocrResult.extracted.total)}</td></tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* PO match */}
                    <div className="rounded-lg p-4" style={{ background: ocrResult.po_found ? '#f0fdf4' : '#fffbeb' }}>
                      <h4 className={`text-xs font-bold uppercase mb-3 ${ocrResult.po_found ? 'text-green-800' : 'text-amber-800'}`}>Purchase Order</h4>
                      {ocrResult.po_found ? (
                        <div>
                          <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                            <div><p className="text-[10px] text-green-600">PO Number</p><p className="font-semibold">{ocrResult.po_data.order_number}</p></div>
                            <div><p className="text-[10px] text-green-600">Supplier</p><p className="font-semibold">{ocrResult.po_data.supplier_name}</p></div>
                            <div><p className="text-[10px] text-green-600">PO Total</p><p className="font-semibold">{currency} {fmtNum(ocrResult.po_data.net_total)}</p></div>
                          </div>
                          <table className="w-full text-[11px] border-collapse bg-white/70 rounded">
                            <thead><tr className="border-b bg-green-100/50"><th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5 w-14">Qty</th><th className="text-right px-2 py-1.5 w-20">Price</th><th className="text-right px-2 py-1.5 w-24">Net</th></tr></thead>
                            <tbody>
                              {(ocrResult.po_data.line_items || []).map((li, i) => (
                                <tr key={i} className="border-b"><td className="px-2 py-1">{li.item_num || i+1}</td><td className="px-2 py-1">{li.item_description}</td><td className="px-2 py-1 text-right">{li.quantity}</td><td className="px-2 py-1 text-right">{fmtNum(li.unit_price)}</td><td className="px-2 py-1 text-right font-semibold">{fmtNum(li.net_price)}</td></tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t"><td colSpan={4} className="px-2 py-1 text-right text-[10px] text-muted-foreground">Subtotal</td><td className="px-2 py-1 text-right">{fmtNum(ocrResult.po_data.net_total)}</td></tr>
                              <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Tax</td><td className="px-2 py-0.5 text-right">{fmtNum(0)}</td></tr>
                              <tr className="border-t-2 font-bold"><td colSpan={4} className="px-2 py-1.5 text-right">Total ({currency})</td><td className="px-2 py-1.5 text-right text-green-700">{fmtNum(ocrResult.po_data.net_total)}</td></tr>
                            </tfoot>
                          </table>
                          {(ocrResult.warnings || []).length > 0 && (
                            <div className="mt-3 space-y-2">
                              {ocrResult.warnings.map((w, i) => (
                                <div key={i} className={`rounded-lg px-3 py-2 text-xs font-semibold ${w.severity === 'error' ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}>
                                  {w.message}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-3">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => createFromOCR(ocrResult.po_data.order_number)}
                              disabled={creating || ocrResult.warnings?.some(w => w.severity === 'error')} data-testid="ocr-create-btn">
                              <Check className="h-3.5 w-3.5 mr-1" /> {creating ? 'Creating...' : ocrResult.warnings?.some(w => w.requires_override) ? 'Override & Create Supplemental Invoice Against This PO' : 'Create Invoice Against This PO'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 mb-3">
                            <p className="text-sm font-semibold text-amber-700">No matching PO found</p>
                            <p className="text-xs text-amber-600 mt-1">Search for a PO manually, or create as a non-PO invoice.</p>
                          </div>
                          <div className="flex gap-2 mb-3">
                            <Input value={poSearchQuery} onChange={e => setPoSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchPOs()} className="h-8 text-sm" placeholder="Search PO number or supplier..." />
                            <Button size="sm" variant="outline" onClick={searchPOs}><Search className="h-3.5 w-3.5" /></Button>
                          </div>
                          {poSearchResults.length > 0 && (
                            <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                              {poSearchResults.map(po => (
                                <button key={po.order_number} onClick={() => createFromOCR(po.order_number)}
                                  className="w-full text-left px-3 py-2 rounded border hover:bg-accent text-xs flex items-center justify-between">
                                  <span><span className="font-mono font-semibold text-primary">{po.order_number}</span> — {po.supplier_name}</span>
                                  <span className="text-muted-foreground">{fmtNum(po.net_total)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {ocrResult.suggested_pos?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-[10px] font-semibold text-muted-foreground mb-1">Suggested matches:</p>
                              {ocrResult.suggested_pos.map(po => (
                                <button key={po.order_number} onClick={() => createFromOCR(po.order_number)}
                                  className="w-full text-left px-3 py-2 rounded border hover:bg-accent text-xs flex items-center justify-between mb-1">
                                  <span className="font-mono font-semibold text-primary">{po.order_number}</span>
                                  <Badge className="text-[9px]">{po.status}</Badge>
                                </button>
                              ))}
                            </div>
                          )}
                          <Button size="sm" variant="outline" onClick={() => createFromOCR('', true)} disabled={creating}>
                            {creating ? 'Creating...' : 'Create Invoice with Ghost PO'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-start gap-2 pt-3 border-t">
                    <Button variant="outline" onClick={() => { setOcrResult(null); setOcrFile(null); }}>Upload Different File</Button>
                    <Button variant="ghost" onClick={() => { setShowOCR(false); setOcrResult(null); setOcrFile(null); }}>Close</Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Spreadsheet Import Dialog ── */}
      {showImport && (
        <Dialog open onOpenChange={() => setShowImport(false)}>
          <DialogContent className="max-w-[90vw] xl:max-w-4xl max-h-[90vh] flex flex-col p-0" data-testid="import-invoice-dialog">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>
                {importStep === 'upload' && 'Import Invoices from Spreadsheet'}
                {importStep === 'map' && 'Map Columns to Invoice Fields'}
                {importStep === 'preview' && 'Review & Import'}
                {importStep === 'result' && 'Import Results'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 pb-5">

              {/* Step 1: Upload */}
              {importStep === 'upload' && (
                <div>
                  <div className="flex gap-3 mb-4">
                    <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                      <Download className="h-3.5 w-3.5 mr-1" /> Download Template
                    </Button>
                    <p className="text-xs text-muted-foreground self-center">Download and fill in the template, or upload any Excel/CSV file and map columns manually.</p>
                  </div>
                  <div className="border-2 border-dashed rounded-xl p-10 text-center mb-4"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                    onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); parseImportFile(e.dataTransfer.files[0]); }}>
                    <p className="text-muted-foreground mb-3">{importFile ? importFile.name : 'Drag & drop Excel or CSV file here.'}</p>
                    <label>
                      <Button size="sm" variant={importFile ? 'outline' : 'default'} onClick={() => {}}><Upload className="h-3.5 w-3.5 mr-1" /> Select File</Button>
                      <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={e => { const f = e.target.files?.[0]; if (f) parseImportFile(f); }} />
                    </label>
                  </div>
                  {creating && <p className="text-sm text-muted-foreground text-center">Parsing file...</p>}
                </div>
              )}

              {/* Step 2: Column Mapping */}
              {importStep === 'map' && importParsed && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Found <strong>{importParsed.row_count}</strong> data row(s) with <strong>{importParsed.columns.length}</strong> columns.
                    Map your spreadsheet columns to invoice fields. Fields marked * are required.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {INVOICE_FIELDS.map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className={`text-xs w-40 shrink-0 ${f.required ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
                          {f.label}{f.required ? ' *' : ''}
                        </Label>
                        <select value={importMappings[f.key] || ''} onChange={e => setImportMappings(m => ({ ...m, [f.key]: e.target.value }))}
                          className="h-8 flex-1 rounded-lg border border-input bg-background px-2 text-xs" data-testid={`mapping-${f.key}`}>
                          <option value="">— Not mapped —</option>
                          {importParsed.columns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {importMappings[f.key] && <Check className="h-4 w-4 text-green-600 shrink-0" />}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1 mb-4">
                    <Label className="text-xs text-muted-foreground">Default Purchase Order (applied to all rows without a PO)</Label>
                    <Input value={importDefaultPO} onChange={e => setImportDefaultPO(e.target.value)} className="h-8 text-sm w-48" placeholder="e.g. PO000001" />
                  </div>
                  <div className="flex justify-start gap-2">
                    <Button onClick={() => setImportStep('preview')} disabled={!importMappings.supplier_invoice_number} data-testid="preview-import-btn">
                      Preview Import
                    </Button>
                    <Button variant="outline" onClick={() => setImportStep('upload')}>Back</Button>
                  </div>
                </div>
              )}

              {/* Step 3: Preview */}
              {importStep === 'preview' && importParsed && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">Preview of first {Math.min(10, importParsed.preview.length)} rows with your mappings applied:</p>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-[11px] border-collapse">
                      <thead><tr className="bg-muted/50 border-b">
                        <th className="px-2 py-1.5 text-left">Invoice #</th>
                        <th className="px-2 py-1.5 text-left">Supplier</th>
                        <th className="px-2 py-1.5 text-left">PO</th>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        <th className="px-2 py-1.5 text-left">Description</th>
                        <th className="px-2 py-1.5 text-right">Qty</th>
                        <th className="px-2 py-1.5 text-right">Price</th>
                        <th className="px-2 py-1.5 text-right">Net</th>
                      </tr></thead>
                      <tbody>
                        {importParsed.preview.slice(0, 10).map((row, i) => {
                          const g = (field) => { const col = importMappings[field]; return col ? (row[col] ?? '') : ''; };
                          return (
                            <tr key={i} className="border-b">
                              <td className="px-2 py-1 font-semibold">{g('supplier_invoice_number')}</td>
                              <td className="px-2 py-1">{g('supplier_name') || g('supplier_code')}</td>
                              <td className="px-2 py-1 font-mono">{g('order_number') || importDefaultPO || '—'}</td>
                              <td className="px-2 py-1">{g('invoice_date')}</td>
                              <td className="px-2 py-1 max-w-[150px] truncate">{g('item_description')}</td>
                              <td className="px-2 py-1 text-right">{g('quantity')}</td>
                              <td className="px-2 py-1 text-right">{g('unit_price')}</td>
                              <td className="px-2 py-1 text-right font-semibold">{g('net_price')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Total rows to import: <strong>{importParsed.row_count}</strong>. Rows with the same Invoice # + Supplier will be grouped as one invoice with multiple lines.</p>
                  <div className="flex justify-start gap-2">
                    <Button onClick={runImport} disabled={importing} data-testid="run-import-btn">
                      {importing ? 'Importing...' : `Import ${importParsed.row_count} Row(s)`}
                    </Button>
                    <Button variant="outline" onClick={() => setImportStep('map')}>Back to Mapping</Button>
                  </div>
                </div>
              )}

              {/* Step 4: Results */}
              {importStep === 'result' && importResult && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="border rounded-lg p-4 bg-green-50">
                      <p className="text-2xl font-bold text-green-700">{importResult.created_count}</p>
                      <p className="text-xs text-green-600">Invoice(s) Created</p>
                    </div>
                    <div className="border rounded-lg p-4 bg-red-50">
                      <p className="text-2xl font-bold text-red-700">{importResult.error_count}</p>
                      <p className="text-xs text-red-600">Error(s)</p>
                    </div>
                  </div>
                  {importResult.created?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold mb-2">Created Invoices:</h4>
                      <div className="space-y-1">
                        {importResult.created.map(c => (
                          <div key={c.invoice_ref} className="flex items-center justify-between px-3 py-2 rounded border bg-green-50 text-xs cursor-pointer hover:bg-green-100"
                            onClick={() => navigate(`/invoices/${c.invoice_ref}`)}>
                            <span className="font-mono font-semibold text-primary">{c.invoice_ref}</span>
                            <span>{c.supplier_invoice_number} — {c.lines} line(s) — {fmtCurrency(c.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-red-600 mb-2">Errors:</h4>
                      <div className="space-y-1">
                        {importResult.errors.map((err, i) => (
                          <div key={i} className="px-3 py-2 rounded border border-red-200 bg-red-50 text-xs text-red-700">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />{err}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => setShowImport(false)} data-testid="close-import-btn">Close</Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}
